import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Calendar,
  Building2,
  User,
  Users,
  Clock,
  MessageSquare,
  UserPlus,
  TrendingUp,
  Archive,
  ArchiveRestore,
  UserCheck,
  Phone,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Separator } from "../../components/ui/separator"
import { Tooltip } from "../../components/ui/tooltip"
import LeadInformationCard from "../../components/forms/LeadInformationCard"
import NotesCard from "../../components/common/NotesCard"
import SMSConversationCard from "../../components/common/SMSConversationCard"
import { leadsApi, Activity } from "../../services/leadsApi"
import { attorneysApi } from "../../services/attorneysApi"
import { firmsApi } from "../../services/firmsApi"
import { usersApi, User as UserType } from "../../services/usersApi"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Toast } from "../../components/ui/toast"
import AssignmentModal from "../../components/modals/AssignmentModal"
import SmsTemplateModal from "../../components/modals/SmsTemplateModal"
import CallPopup from "../../components/modals/CallPopup"
import { callApi } from "../../services/callApi"
interface LeadDetailsData {
  id: string
  first_name: string
  last_name: string
  name: string
  phone: string
  // COMMENTED OUT - Email field temporarily disabled
  email?: string
  source: string
  status?: string
  assigned_to_attorney?: {
    id: string
    name: string
    email: string
    phone: string
  } | null
  referred_to_firm?: {
    id: string
    name: string
    law_firm: string
  } | null
  created_by?: any
  created_at?: string
}

interface Comment {
  id: string
  comment: string
  commented_by: string
  commented_by_display: string
  commented_on: string
  lead: string
}

interface TimelineEvent {
  id: string
  type: "lead_created" | "contact_made" | "proposal_sent" | "meeting_scheduled" | "follow_up" | "qualified" | "converted"
  title: string
  description: string
  date: string
  user: string
  status: "completed" | "pending" | "in_progress"
  icon: React.ReactNode
}

export default function LeadDetails() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [leadDetails, setLeadDetails] = useState<LeadDetailsData | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [userCache, setUserCache] = useState<Record<string, UserType>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [editingData, setEditingData] = useState<LeadDetailsData | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [editErrors, setEditErrors] = useState<{ [key: string]: string[] }>({})
  
  // Email and phone validation states for editing
  // COMMENTED OUT - Email validation temporarily disabled
  // const [emailChecking, setEmailChecking] = useState(false)
  // const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  // const [emailMessage, setEmailMessage] = useState("")
  const [phoneChecking, setPhoneChecking] = useState(false)
  const [phoneAvailable, setPhoneAvailable] = useState<boolean | null>(null)
  const [phoneMessage, setPhoneMessage] = useState("")
  const [phoneFormatError, setPhoneFormatError] = useState(false)
  
  // Toast states
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  // Assignment states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [assignmentType, setAssignmentType] = useState<"attorney" | "firm" | null>(null)
  const [showSmsModal, setShowSmsModal] = useState(false)

  // SMS states
  const [activeTab, setActiveTab] = useState<'notes' | 'sms'>('notes')

  // Call popup states (for WebRTC calls on desktop)
  const [showCallPopup, setShowCallPopup] = useState(false)

  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!state.leadId) {
        setToastMessage("No lead ID provided")
        setToastType('error')
        setShowToast(true)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await leadsApi.getLead(state.leadId)
        setLeadDetails(response.lead)
        setComments(response.comments || [])
      } catch (error) {
        console.error("Error fetching lead details:", error)
        setToastMessage("Failed to load lead details. Please try again.")
        setToastType('error')
        setShowToast(true)
      } finally {
        setLoading(false)
      }
    }

    fetchLeadDetails()
    loadActivities()
  }, [state.leadId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Convert activities to timeline events
  useEffect(() => {
    if (activities.length > 0) {
      const events = activities.map(activity => {
        let title = activity.title
        let description = activity.description || ""
        const icon = getActivityIcon(activity.activity_type)
        const userName = getUserDisplayName(activity.user)

        // For status activities, format the title to show status transitions
        if (activity.activity_type.startsWith('STATUS_')) {
          // Check if this is a "None → new" transition (initial lead creation)
          if (title.toLowerCase().includes('none → new')) {
            title = 'NEW'
          } else {
            // For all other status transitions, show "OLD STATUS → NEW STATUS" in uppercase
            title = title.toUpperCase()
          }
        } else if (activity.activity_type === 'SMS_SENT') {
          // For SMS activities, make the title uppercase
          title = title.toUpperCase()
        } else if (activity.activity_type === 'CALL_ANSWERED' || activity.activity_type === 'CALL_UNANSWERED') {
          // For call activities, make the title uppercase and format nicely
          title = title.toUpperCase()
          // Enhance description for call activities
          if (activity.activity_type === 'CALL_ANSWERED') {
            description = description || "Call was successfully completed"
          } else if (activity.activity_type === 'CALL_UNANSWERED') {
            description = description || "Call was not answered"
          }
        }

        // Determine the type based on the activity type
        let type: TimelineEvent["type"] = "lead_created"
        if (activity.activity_type === "STATUS_ASSIGNED") {
          type = "qualified"
        } else if (activity.activity_type === "STATUS_REFERRED") {
          type = "follow_up"
        } else if (activity.activity_type === "STATUS_ARCHIVED") {
          type = "converted"
        } else if (activity.activity_type === "CALL_ANSWERED" || activity.activity_type === "CALL_UNANSWERED") {
          type = "contact_made"
        } else if (activity.activity_type === "SMS_SENT") {
          type = "contact_made"
        }

        return {
          id: activity.id,
          type,
          title,
          description,
          date: activity.timestamp,
          user: userName,
          status: "completed" as const,
          icon
        }
      })
      
      // Sort events by timestamp (newest first)
      const sortedEvents = events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      setTimelineEvents(sortedEvents)
    }
  }, [activities, userCache, leadDetails]) // eslint-disable-line react-hooks/exhaustive-deps

  // COMMENTED OUT - Email availability check temporarily disabled
  /*
  // Debounced email availability check for editing
  useEffect(() => {
    if (!isEditing || !editingData?.email || editingData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // First validate email format
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    
    if (!emailRegex.test(editingData.email)) {
      setEmailAvailable(null)
      setEmailMessage("Please enter a valid email address")
      return
    }

    // Additional email format checks
    const [localPart, domain] = editingData.email.split('@')
    if (localPart.length > 64) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot exceed 64 characters")
      return
    }
    if (domain.length > 253) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot exceed 253 characters")
      return
    }
    if (localPart.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot be empty")
      return
    }
    if (domain.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot be empty")
      return
    }

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true)
        const response = await leadsApi.checkLeadEmailAvailability(editingData.email, leadDetails?.id)
        setEmailAvailable(response.available)
        setEmailMessage(response.message)
      } catch (error) {
        console.error('Email availability check failed:', error)
        setEmailAvailable(null)
        setEmailMessage("Unable to verify email")
      } finally {
        setEmailChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [editingData?.email, isEditing, leadDetails?.id])
  */

  // Debounced phone availability check for editing
  useEffect(() => {
    if (!isEditing || !editingData?.phone || editingData.phone.length < 10) {
      setPhoneAvailable(null)
      setPhoneMessage("")
      return
    }

    // Format validation is now handled by the child component
    // Only check availability here
    const timer = setTimeout(async () => {
      try {
        setPhoneChecking(true)
        const response = await leadsApi.checkLeadPhoneAvailability(editingData.phone, leadDetails?.id)
        setPhoneAvailable(response.available)
        setPhoneMessage(response.message)
        
        // Clear phone error if phone becomes available
        if (response.available && editErrors.phone) {
          setEditErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.phone
            return newErrors
          })
        }
      } catch (error) {
        console.error('Phone availability check failed:', error)
        setPhoneAvailable(null)
        setPhoneMessage("Unable to verify phone number")
      } finally {
        setPhoneChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [editingData?.phone, isEditing, leadDetails?.id, editErrors.phone])

  const loadActivities = useCallback(async () => {
    if (!state.leadId) return
    
    try {
      const activities = await leadsApi.getLeadActivities(state.leadId)
      setActivities(activities)
      
      // Fetch user details for each unique user in activities
      const uniqueUserIds = Array.from(new Set(activities.map(a => a.user).filter(Boolean) as string[]))
      const userPromises = uniqueUserIds.map(async (userId) => {
        if (!userCache[userId]) {
          try {
            const user = await usersApi.getUser(userId)
            return { userId, user }
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error)
            return { userId, user: null }
          }
        }
        return null
      })
      
      const userResults = await Promise.all(userPromises)
      const newUserCache = { ...userCache }
      userResults.forEach(result => {
        if (result && result.user) {
          newUserCache[result.userId] = result.user
        }
      })
      setUserCache(newUserCache)
    } catch (error) {
      console.error('Failed to load activities:', error)
    }
  }, [state.leadId, userCache])

  const getUserDisplayName = (userId: string | null) => {
    if (!userId) return "CM LAW"
    const user = userCache[userId]
    if (!user) return "Unknown User"
    
    // If the user is an API user (provider), return "CM LAW"
    if (!user.is_staff) {
      return "CM LAW"
    }
    
    // For staff users, return their full name or email
    return `${user.first_name} ${user.last_name}`.trim() || user.email
  }

  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'STATUS_NEW':
        return <UserPlus className="h-4 w-4" />
      case 'STATUS_ASSIGNED':
        return <UserCheck className="h-4 w-4" />
      case 'STATUS_REFERRED':
        return <Building2 className="h-4 w-4" />
      case 'STATUS_ARCHIVED':
        return <Archive className="h-4 w-4" />
      case 'CALL_ANSWERED':
      case 'CALL_UNANSWERED':
        return <Phone className="h-4 w-4" />
      case 'SMS_SENT':
        return <MessageSquare className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const handleBack = () => {
    navigate("/app/leads")
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditingData(leadDetails)
    // COMMENTED OUT - Email validation temporarily disabled
    // Reset email validation when starting to edit
    // setEmailAvailable(null)
    // setEmailMessage("")
    // Reset phone validation when starting to edit
    setPhoneAvailable(null)
    setPhoneMessage("")
    // Reset phone format error when starting to edit
    setPhoneFormatError(false)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingData(null)
    setEditErrors({})
    // COMMENTED OUT - Email validation temporarily disabled
    // Reset email validation when canceling edit
    // setEmailAvailable(null)
    // setEmailMessage("")
    // Reset phone validation when canceling edit
    setPhoneAvailable(null)
    setPhoneMessage("")
    // Reset phone format error when canceling edit
    setPhoneFormatError(false)
  }

  const handleSaveEdit = (data: any) => {
    // Validate required fields with same logic as add-leads.tsx
    const errors: { [key: string]: string[] } = {}
    
    // First name validation
    if (!data.first_name?.trim()) {
      errors.first_name = ["First name is required"]
    } else if (data.first_name.length > 50) {
      errors.first_name = ["First name cannot exceed 50 characters"]
    }
    
    // Last name validation
    if (!data.last_name?.trim()) {
      errors.last_name = ["Last name is required"]
    } else if (data.last_name.length > 50) {
      errors.last_name = ["Last name cannot exceed 50 characters"]
    }
    
    // COMMENTED OUT - Email validation temporarily disabled
    // Email validation (required)
    // if (!data.email?.trim()) {
    //   errors.email = ["Email is required"]
    // } else if (data.email.length > 255) {
    //   errors.email = ["Email cannot exceed 255 characters"]
    // } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(data.email)) {
    //   errors.email = ["Invalid email format"]
    // }
    
    // Phone validation
    if (!data.phone?.trim()) {
      errors.phone = ["Phone number is required"]
    } else {
      // Remove + if present and validate it's all numbers
      const phoneWithoutPlus = data.phone.startsWith('+') ? data.phone.slice(1) : data.phone
      if (!/^\d+$/.test(phoneWithoutPlus)) {
        errors.phone = ["Phone number must contain only numbers (with optional + prefix)"]
      }
    }
    
    // Source validation
    if (!data.source?.trim()) {
      errors.source = ["Source is required"]
    }
    
    // COMMENTED OUT - Email availability check temporarily disabled
    // Check email and phone availability before allowing save
    // if (emailAvailable !== true) {
    //   errors.email = ["Please ensure the email is available before submitting"]
    // }
    if (phoneAvailable === false) {
      errors.phone = ["Please ensure the phone number is available before submitting"]
    }
    
    // Check phone format validation
    if (phoneFormatError) {
      errors.phone = ["Please fix phone number format errors before submitting"]
    }
    
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      return
    }
    
    // Clear errors if validation passes
    setEditErrors({})
    setShowSaveDialog(true)
  }

  const confirmSave = async () => {
    if (!editingData || !leadDetails?.id) return
    
    try {
      // Process phone number - remove + if present (same as add-leads.tsx)
      const processedPhone = editingData.phone.startsWith('+') ? editingData.phone.slice(1) : editingData.phone
      
      // Prepare update data - only include fields that can be updated
      const updateData = {
        first_name: editingData.first_name,
        last_name: editingData.last_name,
        phone: processedPhone,
        // COMMENTED OUT - Email field temporarily disabled
        // email: editingData.email || '',
        source: editingData.source
      }
      
      // Call the API to update the lead
      await leadsApi.updateLead(leadDetails.id, updateData)
      
      // Update local state with the new data
      setLeadDetails(prev => prev ? {
        ...prev,
        first_name: editingData.first_name,
        last_name: editingData.last_name,
        phone: editingData.phone, // Keep the original phone format for display
        // COMMENTED OUT - Email field temporarily disabled
        // email: editingData.email,
        source: editingData.source
      } : null)
      
      // Close edit mode and show success message
      setIsEditing(false)
      setEditingData(null)
      setShowSaveDialog(false)
      // COMMENTED OUT - Email validation temporarily disabled
      // Reset email validation
      // setEmailAvailable(null)
      // setEmailMessage("")
      // Reset phone validation
      setPhoneAvailable(null)
      setPhoneMessage("")
      // Reset phone format error
      setPhoneFormatError(false)
      setToastMessage("Lead updated successfully!")
      setToastType('success')
      setShowToast(true)
    } catch (error: any) {
      console.error("Error updating lead:", error)
      
      // Handle API errors and show them inline
      if (error.message && error.message.includes("HTTP error")) {
        try {
          const errorData = JSON.parse(error.message)
          if (errorData.errors) {
            setEditErrors(errorData.errors)
          } else {
            setToastMessage("Failed to update lead. Please try again.")
            setToastType('error')
            setShowToast(true)
          }
        } catch {
          setToastMessage("Failed to update lead. Please try again.")
          setToastType('error')
          setShowToast(true)
        }
      } else {
        setToastMessage(error.message || "An unexpected error occurred")
        setToastType('error')
        setShowToast(true)
      }
      
      setShowSaveDialog(false)
    }
  }

  const cancelSave = () => {
    setShowSaveDialog(false)
  }

  // Action handlers
  const handleSendSMS = () => {
    setShowSmsModal(true)
  }

  const handleSmsSent = async (template: string, status: 'success' | 'error', message: string) => {
    setToastMessage(message)
    setToastType(status)
    setShowToast(true)
    
    // Refresh activities if SMS was sent successfully
    if (status === 'success') {
      await loadActivities()
    }
  }

  const handleAssignToAttorney = () => {
    handleAssignmentModalOpen("attorney")
  }

  const handleReferToFirm = () => {
    handleAssignmentModalOpen("firm")
  }

  const handleArchiveLead = async () => {
    if (!leadDetails?.id) return
    try {
      await leadsApi.archiveLead(leadDetails.id)
      
      setToastMessage("Lead archived successfully!")
      setToastType('success')
      setShowToast(true)
      
      // Navigate back to leads page after successful archive
      setTimeout(() => {
        navigate("/app/leads")
      }, 1000) // Small delay to show the success message
    } catch (err) {
      console.error('Failed to archive lead:', err)
      setToastMessage("Failed to archive lead. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleUnarchiveLead = async () => {
    if (!leadDetails?.id) return
    try {
      // Determine the new status based on current assignments
      let newStatus = "new" // default status
      let updateData: any = { status: newStatus }
      
      if (leadDetails.assigned_to_attorney) {
        newStatus = "assigned"
        updateData.status = newStatus
      } else if (leadDetails.referred_to_firm) {
        newStatus = "referred"
        updateData.status = newStatus
      }
      
      // Update the lead with the new status
      await leadsApi.updateLead(leadDetails.id, updateData)
      
      setToastMessage("Lead unarchived successfully!")
      setToastType('success')
      setShowToast(true)
      
      // Navigate back to leads page after successful unarchive
      setTimeout(() => {
        navigate("/app/leads")
      }, 1000) // Small delay to show the success message
    } catch (err) {
      console.error('Failed to unarchive lead:', err)
      setToastMessage("Failed to unarchive lead. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleAddComment = async (comment: string) => {
    if (!leadDetails?.id) return
    try {
      await leadsApi.addComment(leadDetails.id, comment)
      // Refresh comments after adding
      const response = await leadsApi.getLeadComments(leadDetails.id)
      setComments(response.comments)
      setToastMessage("Comment added successfully!")
      setToastType('success')
      setShowToast(true)
    } catch (err) {
      console.error('Failed to add comment:', err)
      setToastMessage("Failed to add comment. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }

  // Assignment functions
  const handleAssignmentModalOpen = (type: "attorney" | "firm") => {
    setAssignmentType(type)
    setShowAssignmentModal(true)
  }

  const handleAssignment = async (type: "attorney" | "firm", id: string) => {
    if (!leadDetails?.id) return
    
    try {
      if (type === "attorney") {
        console.log('Assigning lead to attorney:', id)
        await leadsApi.assignToAttorney(leadDetails.id, id)
        console.log('Assignment successful, fetching attorney details...')
        
        // Get attorney details for display
        const attorneyResponse = await attorneysApi.getAttorney(id)
        console.log('Attorney response:', attorneyResponse)
        
        // Handle different possible response structures
        const attorney = attorneyResponse.attorney || attorneyResponse
        
        if (!attorney || !attorney.name) {
          throw new Error('Attorney details not found in response')
        }
        
        setLeadDetails(prev => prev ? {
          ...prev,
          assigned_to_attorney: attorney,
          referred_to_firm: null,
          status: "assigned"
        } : null)
        
        setToastMessage(`Lead assigned to attorney: ${attorney.name}`)
        setToastType('success')
        setShowToast(true)
        
        // Navigate back to leads page after successful assignment
        setTimeout(() => {
          navigate("/app/leads")
        }, 1000) // Small delay to show the success message
      } else if (type === "firm") {
        console.log('Referring lead to firm:', id)
        await leadsApi.referToFirm(leadDetails.id, id)
        console.log('Referral successful, fetching firm details...')
        
        // Get firm details for display
        const firmResponse = await firmsApi.getFirm(id)
        console.log('Firm response:', firmResponse)
        
        // Handle different possible response structures
        const firm = firmResponse.firm || firmResponse
        
        if (!firm || !firm.law_firm) {
          throw new Error('Firm details not found in response')
        }
        
        setLeadDetails(prev => prev ? {
          ...prev,
          referred_to_firm: firm,
          assigned_to_attorney: null,
          status: "referred"
        } : null)
        
        setToastMessage(`Lead referred to firm: ${firm.law_firm}`)
        setToastType('success')
        setShowToast(true)
        
        // Navigate back to leads page after successful referral
        setTimeout(() => {
          navigate("/app/leads")
        }, 1000) // Small delay to show the success message
      }
    } catch (error) {
      console.error("Failed to assign lead:", error)
      setToastMessage("Failed to assign lead. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleRemoveAssignment = async () => {
    if (!leadDetails?.id) return
    
    try {
      await leadsApi.removeAssignment(leadDetails.id)
      
      setLeadDetails(prev => prev ? {
        ...prev,
        assigned_to_attorney: null,
        referred_to_firm: null,
        status: "new"
      } : null)
      
      setToastMessage("Assignment removed successfully!")
      setToastType('success')
      setShowToast(true)
      // Refresh activities to reflect the change immediately
      await loadActivities()
    } catch (error) {
      console.error("Failed to remove assignment:", error)
      setToastMessage("Failed to remove assignment. Please try again.")
      setToastType('error')
      setShowToast(true)
    }
  }

  // Call handler - PSTN calling via agent's phone
  const handlePhoneClick = useCallback(async (leadData: any) => {
    if (!leadDetails?.phone) {
      setToastMessage('No phone number available for this lead')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      // Detect call mode (WebRTC on desktop, PSTN on mobile)
      const callMode = callApi.getCallMode()
      
      if (callMode === 'webrtc') {
        // WebRTC: Browser-based call
        // Request microphone permission first
        const micPermission = await callApi.requestMicrophonePermission()
        if (!micPermission) {
          setToastMessage('Microphone access denied. Please enable microphone permissions.')
          setToastType('error')
          setShowToast(true)
          return
        }

        // Ensure device exists, but don't re-create if it's already registered
        if (!callApi.isReady()) {
          console.log('🔄 Device not ready, refreshing...')
          await callApi.refreshDevice()
        } else {
          console.log('✅ Device already registered, skipping refresh')
        }

        // Prevent duplicate call if one is active
        if (callApi.hasActiveCall()) {
          console.warn('⚠️ A call is already active, ignoring new request')
          setToastMessage('A call is already in progress')
          setToastType('error')
          setShowToast(true)
          return
        }

        setShowCallPopup(true)

        // Initiate WebRTC call (popup will show progress)
        await callApi.makeCall(leadDetails.phone)
      } else {
        // PSTN: Phone-based call
        setToastMessage('📞 Calling your phone...')
        setToastType('success')
        setShowToast(true)

        // Initiate PSTN call
        await callApi.makeCall(leadDetails.phone)

        // Update toast on success
        setTimeout(() => {
          setToastMessage(`✓ Pick up your phone to connect to ${leadDetails.name}`)
          setToastType('success')
          setShowToast(true)
        }, 1500)
      }

      // Refresh lead details after a delay (call might be completed)
      setTimeout(async () => {
        if (state.leadId) {
          try {
            const response = await leadsApi.getLead(state.leadId)
            setLeadDetails(response.lead)
            await loadActivities()
          } catch (error) {
            console.error('Failed to refresh lead details:', error)
          }
        }
      }, 5000)

    } catch (error: any) {
      setToastMessage(`Call failed: ${error.message || 'Unknown error'}`)
      setToastType('error')
      setShowToast(true)
    }
  }, [leadDetails, state.leadId, loadActivities])

  const formatUSTimestamp = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading lead details...</p>
        </div>
      </div>
    )
  }

  if (!leadDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Lead not found</p>
          <Button onClick={handleBack} className="mt-4">
            Back to Leads
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleBack} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Back to Leads</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-2xl font-bold text-slate-900">Lead Details</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* All Action Buttons - Desktop */}
              <div className="hidden lg:flex items-center gap-2">
                <Button onClick={handleSendSMS} variant="outline" size="sm" className="border-slate-300 hover:border-slate-400">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send SMS
                </Button>
                <Button onClick={handleAssignToAttorney} variant="outline" size="sm" className="border-slate-300 hover:border-slate-400">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Assign to Attorney
                </Button>
                <Button onClick={handleReferToFirm} variant="outline" size="sm" className="border-slate-300 hover:border-slate-400">
                  <Building2 className="h-4 w-4 mr-2" />
                  Refer to Firm
                </Button>
                {leadDetails?.status === 'archived' ? (
                  <Button onClick={handleUnarchiveLead} variant="outline" size="sm" className="border-green-200 hover:border-green-300 hover:bg-green-50 text-green-700 hover:text-green-800">
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    Unarchive Lead
                  </Button>
                ) : (
                  <Button onClick={handleArchiveLead} variant="outline" size="sm" className="border-red-200 hover:border-red-300 hover:bg-red-50 text-red-700 hover:text-red-800">
                    <Archive className="h-4 w-4 mr-2" />
                    Archive Lead
                  </Button>
                )}
              </div>
              
              {/* Action Icons - Mobile/Tablet */}
              <div className="lg:hidden flex items-center gap-4">
                <Tooltip content="Send SMS" side="bottom">
                  <MessageSquare 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={handleSendSMS}
                  />
                </Tooltip>
                <Tooltip content="Assign to attorney" side="bottom">
                  <UserCheck 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={handleAssignToAttorney}
                  />
                </Tooltip>
                <Tooltip content="Refer to firm" side="bottom">
                  <Building2 
                    className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                    onClick={handleReferToFirm}
                  />
                </Tooltip>
                <Tooltip content={leadDetails?.status === 'archived' ? 'Unarchive' : 'Archive'} side="bottom">
                  {leadDetails?.status === 'archived' ? (
                    <ArchiveRestore 
                      className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                      onClick={handleUnarchiveLead}
                    />
                  ) : (
                    <Archive 
                      className="h-5 w-5 text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" 
                      onClick={handleArchiveLead}
                    />
                  )}
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Details */}
            <LeadInformationCard
              leadData={{
                first_name: editingData ? editingData.first_name : leadDetails.first_name,
                last_name: editingData ? editingData.last_name : leadDetails.last_name,
                phone: editingData ? editingData.phone : leadDetails.phone,
                // COMMENTED OUT - Email field temporarily disabled
                // email: editingData ? editingData.email : leadDetails.email,
                source: editingData ? editingData.source : leadDetails.source,
              }}
              viewOnly={true}
              isEditing={isEditing}
              onEdit={handleEdit}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              onDataChange={(field, value) => {
                if (editingData) {
                  setEditingData({ ...editingData, [field]: value })
                  // Clear error for this field when user starts typing
                  if (editErrors[field]) {
                    setEditErrors(prev => ({ ...prev, [field]: [] }))
                  }
                  
                  // Reset validation states when email or phone changes
                  // COMMENTED OUT - Email validation temporarily disabled
                  // if (field === 'email') {
                  //   setEmailAvailable(null)
                  //   setEmailMessage("")
                  // } else 
                  if (field === 'phone') {
                    setPhoneAvailable(null)
                    setPhoneMessage("")
                  }
                  
                  // Real-time validation for length limits
                  if (field === 'first_name' && value.length > 50) {
                    setEditErrors(prev => ({ ...prev, first_name: ["First name cannot exceed 50 characters"] }))
                  } else if (field === 'last_name' && value.length > 50) {
                    setEditErrors(prev => ({ ...prev, last_name: ["Last name cannot exceed 50 characters"] }))
                  } 
                  // COMMENTED OUT - Email validation temporarily disabled
                  // else if (field === 'email') {
                  //   // Real-time email validation - only check length, format validation is handled by useEffect
                  //   if (!value || !value.trim()) {
                  //     setEditErrors(prev => ({ ...prev, email: ["Email is required"] }))
                  //   } else if (value.length > 255) {
                  //     setEditErrors(prev => ({ ...prev, email: ["Email cannot exceed 255 characters"] }))
                  //   } else if (editErrors.email) {
                  //     setEditErrors(prev => ({ ...prev, email: [] }))
                  //   }
                  // } 
                  else if (field === 'phone') {
                    // Clear any existing phone errors when phone changes
                    if (editErrors.phone) {
                      setEditErrors(prev => ({ ...prev, phone: [] }))
                    }
                    // Reset phone format error when phone changes
                    setPhoneFormatError(false)
                  }
                }
              }}
              errors={editErrors}
              // COMMENTED OUT - Email validation temporarily disabled
              // emailValidation={isEditing ? {
              //   emailChecking,
              //   emailAvailable,
              //   emailMessage
              // } : undefined}
              phoneValidation={isEditing ? {
                phoneChecking,
                phoneAvailable,
                phoneMessage
              } : undefined}
              onPhoneFormatValidationChange={setPhoneFormatError}
              onPhoneClick={handlePhoneClick}
            />

            {/* Historical Tracking Timeline */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
                  <TrendingUp className="h-5 w-5" />
                  Lead Progress Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  
                  {/* Timeline Events */}
                  <div className="space-y-6">
                    {timelineEvents.map((event, index) => (
                      <div key={event.id} className="relative flex items-start">
                        {/* Timeline Dot */}
                        <div className={`absolute left-5 top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-green-500`}></div>
                        
                        {/* Event Content */}
                        <div className="ml-12 flex-1">
                          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-slate-300 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-white rounded-md border border-slate-200">
                                  {event.icon}
                                </div>
                                <h4 className="font-medium text-slate-900">{event.title}</h4>
                              </div>
                            </div>
                            
                            <p className="text-sm text-slate-600 mb-3">{event.description}</p>
                            
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {event.user}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatUSTimestamp(event.date)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Notes/SMS Tabs */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200 pb-0">
                <div className="flex space-x-1">
                  <button
                    onClick={() => setActiveTab('notes')}
                    className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      activeTab === 'notes'
                        ? 'bg-slate-100 text-slate-900 border-b-2 border-slate-900'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Notes
                  </button>
                  <button
                    onClick={() => setActiveTab('sms')}
                    className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      activeTab === 'sms'
                        ? 'bg-slate-100 text-slate-900 border-b-2 border-slate-900'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    SMS
                  </button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {activeTab === 'notes' ? (
                  <NotesCard
                    comments={comments}
                    onAddComment={handleAddComment}
                  />
                ) : (
                  <SMSConversationCard
                    leadId={leadDetails.id}
                    leadName={leadDetails.name}
                    leadPhone={leadDetails.phone}
                  />
                )}
              </CardContent>
            </Card>

            {/* Assignment Card */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
                  <UserCheck className="h-5 w-5" />
                  Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Current Assignment Status */}
                  {leadDetails?.assigned_to_attorney && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-2">
                        <UserCheck className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-green-900">Assigned to Attorney</p>
                          <p className="text-sm text-green-700">{leadDetails.assigned_to_attorney.name}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAssignment}
                        className="mt-3 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        Remove Assignment
                      </Button>
                    </div>
                  )}

                  {leadDetails?.referred_to_firm && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">Referred to Firm</p>
                          <p className="text-sm text-blue-700">{leadDetails.referred_to_firm.law_firm}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAssignment}
                        className="mt-3 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                      >
                        Remove Assignment
                      </Button>
                    </div>
                  )}

                  {!leadDetails?.assigned_to_attorney && !leadDetails?.referred_to_firm && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-gray-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Not Assigned</p>
                          <p className="text-sm text-gray-700">Lead is not currently assigned</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assignment Actions */}
                  <div className="space-y-2">
                    <Button 
                      onClick={handleAssignToAttorney}
                      className="w-full bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      Assign to Attorney
                    </Button>
                    <Button 
                      onClick={handleReferToFirm}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Refer to Firm
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogDescription>
                Are you sure you want to save the changes to this lead? This will update the lead information permanently.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={cancelSave}>
                Cancel
              </Button>
              <Button onClick={confirmSave} className="bg-green-600 hover:bg-green-700">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Modal */}
        <AssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          assignmentType={assignmentType}
          onAssignment={handleAssignment}
          currentAssignment={
            assignmentType === "attorney" && leadDetails?.assigned_to_attorney
              ? {
                  type: "attorney" as const,
                  id: leadDetails.assigned_to_attorney.id,
                  name: leadDetails.assigned_to_attorney.name
                }
              : assignmentType === "firm" && leadDetails?.referred_to_firm
              ? {
                  type: "firm" as const,
                  id: leadDetails.referred_to_firm.id,
                  name: leadDetails.referred_to_firm.name
                }
              : null
          }
          onRemoveAssignment={
            leadDetails?.id
              ? async () => {
                  await leadsApi.removeAssignment(leadDetails.id)
                  setLeadDetails(prev => prev ? {
                    ...prev,
                    assigned_to_attorney: null,
                    referred_to_firm: null,
                    status: "new"
                  } : null)
                  setToastMessage("Assignment removed successfully")
                  setToastType('success')
                  setShowToast(true)
                }
              : undefined
          }
        />

        {/* SMS Template Modal */}
        {leadDetails && (
          <SmsTemplateModal
            isOpen={showSmsModal}
            onClose={() => setShowSmsModal(false)}
            lead={leadDetails}
            onSmsSent={handleSmsSent}
          />
        )}

        {/* Call Popup (WebRTC only - shows on desktop) */}
        {leadDetails && (
          <CallPopup
            callerName={leadDetails.name || 'Unknown'}
            callerPhone={leadDetails.phone}
            isVisible={showCallPopup}
            onHangUp={() => {
              setShowCallPopup(false)
              callApi.hangUp()
            }}
            onCallError={(error) => {
              setToastMessage(`Call error: ${error}`)
              setToastType('error')
              setShowToast(true)
              setShowCallPopup(false)
            }}
          />
        )}

        {/* Toast Notification */}
        <Toast
          message={toastMessage}
          type={toastType}
          isVisible={showToast}
          onClose={() => setShowToast(false)}
        />
      </div>
    </div>
  )
}
